import { Gql } from '@fiverr-private/dynamic_matching';
import { BudgetAndTimelineContent, durations, timeCommitments, timeUnits } from './types';
import { useNeedType } from './shared';

export const hasOneTimeFixedPriceShape = (obj: any): boolean => {
    if (!obj || typeof obj !== 'object') {
        return false;
    }

    const { pricingType, budgetInCents, timeline, deliveryDate, timelineDetails } = obj;

    const isFixedPrice = pricingType === Gql.BriefPricingType.Fixed;
    const hasBudget = typeof budgetInCents === 'number';
    const isValidTimeline = Object.values(Gql.DmBriefTimeline)
        .filter((timeline) => timeline !== Gql.DmBriefTimeline.Ongoing)
        .includes(timeline);

    if (!isFixedPrice || !hasBudget || !isValidTimeline) {
        return false;
    }

    if (timeline === Gql.DmBriefTimeline.SpecificDate) {
        return typeof deliveryDate === 'number';
    }

    if (timeline === Gql.DmBriefTimeline.GeneralTimeline) {
        const generalTimeline = timelineDetails?.generalTimeline;
        return (
            generalTimeline &&
            typeof generalTimeline === 'object' &&
            typeof generalTimeline.value === 'number' &&
            Object.values(timeUnits).includes(generalTimeline.unit)
        );
    }

    return true;
};

export const hasOneTimeHourlyShape = (obj: any): boolean => {
    if (!obj || typeof obj !== 'object') {
        return false;
    }

    const { pricingType, hourlyBudgetRange, timeline, deliveryDate, timelineDetails } = obj;

    const isHourly = pricingType === Gql.BriefPricingType.Hourly;

    const hasValidHourlyRange =
        hourlyBudgetRange &&
        typeof hourlyBudgetRange === 'object' &&
        typeof hourlyBudgetRange.min === 'number' &&
        typeof hourlyBudgetRange.max === 'number';

    const isValidTimeline = Object.values(Gql.DmBriefTimeline)
        .filter((timeline) => timeline !== Gql.DmBriefTimeline.Ongoing)
        .includes(timeline);

    if (!isHourly || !hasValidHourlyRange || !isValidTimeline) {
        return false;
    }

    if (timeline === Gql.DmBriefTimeline.SpecificDate) {
        return typeof deliveryDate === 'number';
    }

    if (timeline === Gql.DmBriefTimeline.GeneralTimeline) {
        const generalTimeline = timelineDetails?.generalTimeline;
        return (
            generalTimeline &&
            typeof generalTimeline === 'object' &&
            typeof generalTimeline.value === 'number' &&
            Object.values(timeUnits).includes(generalTimeline.unit)
        );
    }

    return true;
};

export const hasOngoingFixedPriceShape = (obj: any): boolean => {
    if (!obj || typeof obj !== 'object') {
        return false;
    }

    const { pricingType, budgetInCents, timeline, timelineDetails } = obj;

    const isFixed = pricingType === Gql.BriefPricingType.Fixed;
    const isOngoing = timeline === Gql.DmBriefTimeline.Ongoing;
    const hasBudget = typeof budgetInCents === 'number';

    const hasValidHiringDuration =
        timelineDetails?.hiringDuration && Object.values(durations).includes(timelineDetails.hiringDuration);

    const hasValidDeliveryFrequency =
        timelineDetails?.deliveriesFrequency &&
        typeof timelineDetails.deliveriesFrequency === 'object' &&
        typeof timelineDetails.deliveriesFrequency.value === 'number' &&
        Object.values(timeUnits).includes(timelineDetails.deliveriesFrequency.unit);

    const hasValidTimelineDetails =
        timelineDetails && typeof timelineDetails === 'object' && hasValidHiringDuration && hasValidDeliveryFrequency;

    return isFixed && isOngoing && hasBudget && hasValidTimelineDetails;
};

export const hasOngoingHourlyShape = (obj: any): boolean => {
    if (!obj || typeof obj !== 'object') {
        return false;
    }

    const { pricingType, hourlyBudgetRange, timeline, timelineDetails } = obj;

    const isHourly = pricingType === Gql.BriefPricingType.Hourly;
    const isOngoing = timeline === Gql.DmBriefTimeline.Ongoing;

    const hasValidHourlyRange =
        hourlyBudgetRange &&
        typeof hourlyBudgetRange === 'object' &&
        typeof hourlyBudgetRange.min === 'number' &&
        typeof hourlyBudgetRange.max === 'number';

    const hasValidTimeCommitment =
        timelineDetails?.timeCommitment && Object.values(timeCommitments).includes(timelineDetails.timeCommitment);

    const hasValidHiringDuration =
        timelineDetails?.hiringDuration && Object.values(durations).includes(timelineDetails.hiringDuration);

    const hasValidTimelineDetails =
        timelineDetails && typeof timelineDetails === 'object' && hasValidTimeCommitment && hasValidHiringDuration;

    return isHourly && isOngoing && hasValidHourlyRange && hasValidTimelineDetails;
};

export const isValidContentStructure = (content: BudgetAndTimelineContent): boolean => {
    const { needType } = useNeedType();
    const { pricingType } = content;

    if (!pricingType || !needType) {
        return false;
    }

    if (needType === Gql.DmBriefNeedType.OneTime) {
        return pricingType === Gql.BriefPricingType.Fixed
            ? hasOneTimeFixedPriceShape(content)
            : hasOneTimeHourlyShape(content);
    } else {
        return pricingType === Gql.BriefPricingType.Fixed
            ? hasOngoingFixedPriceShape(content)
            : hasOngoingHourlyShape(content);
    }
};
